import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package, AlertTriangle, TrendingUp, TrendingDown, Search, Plus,
  Minus, Camera, X, Loader2, ArrowUpDown, History, Boxes,
  ScanLine, Check, Upload, RotateCcw, XCircle, RefreshCw, ShoppingCart,
  Edit2, CheckSquare, Square, Save, Trash2, Download, FileDown
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import ShopLayout from '@/components/shop/ShopLayout';
import CameraBarcodeScanner from '@/components/shop/CameraBarcodeScanner';
import { toast } from 'sonner';

const MOVEMENT_TYPES = {
  intake: { label: 'stock_intake', color: 'text-green-600', icon: TrendingUp },
  sale: { label: 'stock_sale', color: 'text-blue-600', icon: TrendingDown },
  adjustment: { label: 'stock_adjustment', color: 'text-amber-600', icon: ArrowUpDown },
  loss: { label: 'stock_loss', color: 'text-red-600', icon: XCircle },
  return: { label: 'stock_return', color: 'text-purple-600', icon: RotateCcw },
};

const StockPage = () => {
  const { api } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState({});
  const [lowStock, setLowStock] = useState([]);
  const [movements, setMovements] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  // Adjust modal
  const [adjustProduct, setAdjustProduct] = useState(null);
  const [adjustQty, setAdjustQty] = useState('');
  const [adjustType, setAdjustType] = useState('intake');
  const [adjustNotes, setAdjustNotes] = useState('');
  const [adjustSupplier, setAdjustSupplier] = useState('');
  const [adjustCost, setAdjustCost] = useState('');
  const [adjustSalePrice, setAdjustSalePrice] = useState('');
  const [saving, setSaving] = useState(false);

  // Scanner
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanPreview, setScanPreview] = useState(null);
  const [scanSalePrices, setScanSalePrices] = useState({});
  const [scanEdits, setScanEdits] = useState({});

  // Reorder
  const [reorderData, setReorderData] = useState(null);

  // Camera scanner
  const [showCameraScanner, setShowCameraScanner] = useState(false);

  // Inline editing
  const [editingRowId, setEditingRowId] = useState(null);
  const [editValues, setEditValues] = useState({});

  // Bulk selection & editing
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkValues, setBulkValues] = useState({ stock_count: '', cost_price: '', price: '' });
  const [bulkSaving, setBulkSaving] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const shopsRes = await api.get('/shops');
      if (shopsRes.data.length === 0) { navigate('/shop/setup'); return; }
      const s = shopsRes.data[0];
      setShop(s);

      const [stockRes, movRes] = await Promise.all([
        api.get(`/shops/${s.id}/stock/overview`),
        api.get(`/shops/${s.id}/stock/movements?limit=30`),
      ]);

      setProducts(stockRes.data.products);
      setStats(stockRes.data.stats);
      setLowStock(stockRes.data.low_stock_products);
      setMovements(movRes.data);

      api.get(`/shops/${s.id}/stock/reorder-suggestions`).then(r => setReorderData(r.data)).catch(() => {});
    } catch (e) { console.error('Failed to load stock data:', e); }
    finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    if (!searchQuery) return products;
    const q = searchQuery.toLowerCase();
    return products.filter(p =>
      p.name.toLowerCase().includes(q) ||
      p.category?.toLowerCase().includes(q) ||
      p.barcode?.toLowerCase().includes(q)
    );
  }, [products, searchQuery]);

  // --- Inline Edit Handlers ---
  const startEdit = (p) => {
    setEditingRowId(p.id);
    setEditValues({
      stock_count: p.stock_count ?? '',
      cost_price: p.cost_price ?? '',
      price: p.price ?? '',
    });
  };

  const cancelEdit = () => {
    setEditingRowId(null);
    setEditValues({});
  };

  const saveEdit = async (productId) => {
    setSaving(true);
    try {
      const payload = {};
      if (editValues.stock_count !== '') payload.stock_count = parseInt(editValues.stock_count);
      if (editValues.cost_price !== '') payload.cost_price = parseFloat(editValues.cost_price);
      if (editValues.price !== '') payload.price = parseFloat(editValues.price);

      await api.put(`/shops/${shop.id}/products/${productId}`, payload);
      toast.success(t('stock_saved') || 'Product updated');
      setEditingRowId(null);
      setEditValues({});
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error saving');
    } finally { setSaving(false); }
  };

  // --- Bulk Edit Handlers ---
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(p => p.id)));
    }
  };

  const openBulkEdit = () => {
    setBulkValues({ stock_count: '', cost_price: '', price: '' });
    setShowBulkEdit(true);
  };

  const saveBulkEdit = async () => {
    if (selectedIds.size === 0) return;
    setBulkSaving(true);
    try {
      const items = [...selectedIds].map(product_id => {
        const item = { product_id };
        if (bulkValues.stock_count !== '') item.stock_count = parseInt(bulkValues.stock_count);
        if (bulkValues.cost_price !== '') item.cost_price = parseFloat(bulkValues.cost_price);
        if (bulkValues.price !== '') item.price = parseFloat(bulkValues.price);
        return item;
      });

      await api.post(`/shops/${shop.id}/products/bulk-update`, { items });
      toast.success(`${selectedIds.size} products updated`);
      setShowBulkEdit(false);
      setSelectedIds(new Set());
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk update failed');
    } finally { setBulkSaving(false); }
  };

  // --- Delete Selected ---
  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected products? This cannot be undone.`)) return;
    try {
      await api.post(`/shops/${shop.id}/products/bulk-delete`, { product_ids: [...selectedIds] });
      toast.success(`Deleted ${selectedIds.size} products`);
      setSelectedIds(new Set());
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  // --- Export ---
  const handleExportCSV = () => {
    const ids = selectedIds.size > 0 ? [...selectedIds].join(',') : '';
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/shops/${shop.id}/stock/export-csv${ids ? `?product_ids=${ids}` : ''}`;
    window.open(url, '_blank');
  };

  const handleExportPDF = () => {
    const ids = selectedIds.size > 0 ? [...selectedIds].join(',') : '';
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/shops/${shop.id}/stock/export-pdf${ids ? `?product_ids=${ids}` : ''}`;
    window.open(url, '_blank');
  };

  // --- Stock Adjust ---
  const handleAdjust = async () => {
    if (!adjustProduct || !adjustQty) return;
    setSaving(true);
    const qty = parseInt(adjustQty);
    const finalQty = ['loss', 'sale'].includes(adjustType) ? -Math.abs(qty) : Math.abs(qty);
    try {
      await api.post(`/shops/${shop.id}/stock/movements`, {
        product_id: adjustProduct.id,
        quantity: finalQty,
        movement_type: adjustType,
        notes: adjustNotes || null,
        supplier: adjustSupplier || null,
        cost_per_unit: adjustCost ? parseFloat(adjustCost) : null,
        sale_price: adjustSalePrice ? parseFloat(adjustSalePrice) : null,
      });
      setAdjustProduct(null);
      setAdjustQty('');
      setAdjustNotes('');
      setAdjustSupplier('');
      setAdjustCost('');
      setAdjustSalePrice('');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  // --- Scanner ---
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setScanPreview(reader.result);
    reader.readAsDataURL(file);

    const toBase64 = (f) => new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result.split(',')[1]);
      r.onerror = reject;
      r.readAsDataURL(f);
    });

    setScanning(true);
    setScanResult(null);
    try {
      const b64 = await toBase64(file);
      const res = await api.post(`/shops/${shop.id}/stock/scan-receipt`, { image_base64: b64 });
      setScanResult(res.data);
      const prices = {};
      (res.data.matched_items || []).forEach((item, i) => {
        if (item.existing_sale_price) prices[i] = item.existing_sale_price;
      });
      setScanSalePrices(prices);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed');
      setScanResult(null);
    } finally { setScanning(false); }
  };

  const handleBulkIntake = async () => {
    if (!scanResult?.matched_items?.length) return;
    setSaving(true);
    try {
      await api.post(`/shops/${shop.id}/stock/bulk-intake`, {
        items: scanResult.matched_items.map((item, i) => ({
          product_id: item.product_id,
          quantity: item.quantity || 1,
          movement_type: 'intake',
          supplier: scanResult.supplier_name,
          cost_per_unit: item.cost_per_unit,
          sale_price: scanSalePrices[i] || item.existing_sale_price || null,
          notes: `AI Receipt Scan - ${scanResult.supplier_name || 'Unknown'}`,
        })),
        supplier: scanResult.supplier_name,
        receipt_date: scanResult.receipt_date,
      });
      toast.success(`${scanResult.matched_items.length} matched items added to stock`);
      setScanResult(null);
      setScanPreview(null);
      setScanSalePrices({});
      setScanEdits({});
      setActiveTab('overview');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const handleCreateUnmatched = async () => {
    if (!scanResult?.unmatched_items?.length) return;
    setSaving(true);
    try {
      let created = 0;
      for (let i = 0; i < scanResult.unmatched_items.length; i++) {
        const item = scanResult.unmatched_items[i];
        const edits = scanEdits[`u${i}`] || {};
        const qty = parseInt(edits.quantity ?? item.quantity) || 1;
        const name = edits.name || item.name;
        const cost = parseFloat(edits.cost_per_unit ?? item.cost_per_unit) || 0;
        const taxStr = item.tax_rate || '21%';
        const taxNum = parseFloat(taxStr.replace('%', '')) || 21;
        await api.post(`/shops/${shop.id}/products`, {
          name,
          category: item.category || 'general',
          price: cost,
          cost_price: cost,
          unit: edits.unit || item.unit || 'unidad',
          stock_count: qty,
          tax_rate: taxNum,
          description: `Receipt scan - ${scanResult.supplier_name || 'Unknown'}${item.notes ? ` (${item.notes})` : ''}`,
        });
        created++;
      }
      toast.success(`${created} new products created and stocked`);
      setScanResult(null);
      setScanPreview(null);
      setScanSalePrices({});
      setScanEdits({});
      setActiveTab('overview');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || 'Error creating products'); }
    finally { setSaving(false); }
  };

  const stockLevel = (p) => {
    if (p.stock_count === null || p.stock_count === undefined) return 'unlimited';
    if (p.stock_count === 0) return 'out';
    if (p.stock_count <= (p.low_stock_threshold || 5)) return 'low';
    return 'ok';
  };

  const stockBadge = (level) => {
    switch (level) {
      case 'out': return 'bg-red-100 text-red-700';
      case 'low': return 'bg-amber-100 text-amber-700';
      case 'ok': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-500';
    }
  };

  if (loading) {
    return <ShopLayout><div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" /></div></ShopLayout>;
  }

  return (
    <ShopLayout>
      <div className="space-y-6" data-testid="stock-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-[#1e3a5f]">{t('stock_title') || 'Stock Management'}</h2>
            <p className="text-slate-500 text-sm">{stats.tracked_products || 0} {t('stock_tracked') || 'tracked products'}</p>
          </div>
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-[#1e3a5f]">{selectedIds.size} selected</span>
              <button onClick={openBulkEdit}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#1e3a5f] text-white text-sm font-medium hover:bg-[#2a4f7f] transition-colors"
                data-testid="bulk-edit-btn">
                <Edit2 className="w-4 h-4" /> Bulk Edit
              </button>
              <button onClick={handleDeleteSelected}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
                data-testid="bulk-delete-btn">
                <Trash2 className="w-4 h-4" /> Delete
              </button>
              <button onClick={handleExportCSV}
                className="inline-flex items-center gap-2 px-3 py-2 bg-white border-2 border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                data-testid="export-csv-btn">
                <Download className="w-4 h-4" /> CSV
              </button>
              <button onClick={handleExportPDF}
                className="inline-flex items-center gap-2 px-3 py-2 bg-white border-2 border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                data-testid="export-pdf-btn">
                <FileDown className="w-4 h-4" /> PDF
              </button>
              <button onClick={() => setSelectedIds(new Set())}
                className="px-3 py-2 text-sm text-slate-500 hover:text-slate-700"
                data-testid="clear-selection-btn">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {selectedIds.size === 0 && (
            <div className="flex items-center gap-2">
              <button onClick={handleExportCSV}
                className="inline-flex items-center gap-2 px-3 py-2 bg-white border-2 border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
                data-testid="export-all-csv-btn">
                <Download className="w-4 h-4" /> Export CSV
              </button>
              <button onClick={handleExportPDF}
                className="inline-flex items-center gap-2 px-3 py-2 bg-white border-2 border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
                data-testid="export-all-pdf-btn">
                <FileDown className="w-4 h-4" /> Export PDF
              </button>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: t('stock_total') || 'Total Products', value: stats.total_products, icon: Package, color: 'bg-blue-50 text-blue-600' },
            { label: t('stock_tracked') || 'Tracked', value: stats.tracked_products, icon: Boxes, color: 'bg-[#1e3a5f]/5 text-[#1e3a5f]' },
            { label: t('stock_low') || 'Low Stock', value: stats.low_stock_count, icon: AlertTriangle, color: 'bg-amber-50 text-amber-600' },
            { label: t('stock_out') || 'Out of Stock', value: stats.out_of_stock_count, icon: XCircle, color: 'bg-red-50 text-red-600' },
          ].map((card, i) => (
            <div key={i} className="bg-white border-2 border-slate-100 p-4" data-testid={`stock-stat-${i}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${card.color}`}>
                  <card.icon className="w-5 h-5" />
                </div>
              </div>
              <p className="text-2xl font-bold text-[#1e3a5f]">{card.value || 0}</p>
              <p className="text-sm text-slate-500">{card.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b-2 border-slate-100">
          {[
            { id: 'overview', label: t('stock_overview') || 'Stock Overview', icon: Boxes },
            { id: 'movements', label: t('stock_history') || 'Movement History', icon: History },
            { id: 'scanner', label: t('stock_scanner') || 'AI Receipt Scanner', icon: ScanLine },
            { id: 'reorder', label: t('stock_reorder') || 'Reorder Suggestions', icon: RefreshCw },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-[2px] transition-colors ${
                activeTab === tab.id
                  ? 'border-[#22c55e] text-[#1e3a5f]'
                  : 'border-transparent text-slate-400 hover:text-slate-600'
              }`} data-testid={`stock-tab-${tab.id}`}>
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content: Overview */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {/* Low Stock Alerts */}
            {lowStock.length > 0 && (
              <div className="bg-amber-50 border-2 border-amber-200 p-4" data-testid="low-stock-alert">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  <span className="font-semibold text-amber-800">{t('stock_alert_title') || 'Low Stock Alert'}</span>
                  <span className="text-sm text-amber-600">({lowStock.length} {t('stock_items') || 'items'})</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {lowStock.slice(0, 8).map(p => (
                    <span key={p.id} className="px-3 py-1 bg-white border border-amber-300 text-sm text-amber-800 rounded-sm">
                      {p.name}: <strong>{p.stock_count}</strong>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Search */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  placeholder={t('stock_search') || 'Search products...'}
                  className="w-full pl-10 pr-4 py-2.5 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none"
                  data-testid="stock-search" />
              </div>
              <button
                onClick={() => setShowCameraScanner(!showCameraScanner)}
                className={`px-3 py-2 border-2 flex items-center gap-2 text-sm font-medium transition-colors ${
                  showCameraScanner
                    ? 'bg-[#1e3a5f] border-[#1e3a5f] text-white'
                    : 'bg-white border-slate-200 text-slate-600 hover:border-[#1e3a5f]/30'
                }`}
                data-testid="stock-camera-scan-btn"
              >
                <ScanLine className="w-4 h-4" /> Camera
              </button>
            </div>

            {/* Camera Scanner */}
            {showCameraScanner && (
              <div className="max-w-sm" data-testid="stock-camera-scanner-wrapper">
                <CameraBarcodeScanner
                  onScan={(barcode) => {
                    setShowCameraScanner(false);
                    const product = products.find(p => p.barcode === barcode);
                    if (product) {
                      setAdjustProduct(product);
                      setAdjustType('intake');
                      setAdjustQty('');
                      toast.success(`Found: ${product.name}`);
                    } else {
                      toast.error(`No product found with barcode: ${barcode}`);
                    }
                  }}
                  onClose={() => setShowCameraScanner(false)}
                />
              </div>
            )}

            {/* Products Table */}
            <div className="bg-white border-2 border-slate-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="stock-table">
                  <thead>
                    <tr className="border-b-2 border-slate-100 text-left">
                      <th className="px-3 py-3 w-10">
                        <button onClick={toggleSelectAll} data-testid="select-all-btn" className="p-0.5">
                          {selectedIds.size === filtered.length && filtered.length > 0
                            ? <CheckSquare className="w-4 h-4 text-[#1e3a5f]" />
                            : <Square className="w-4 h-4 text-slate-300" />}
                        </button>
                      </th>
                      <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('stock_product') || 'Product'}</th>
                      <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('stock_category') || 'Category'}</th>
                      <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('stock_cost_price') || 'Cost'}</th>
                      <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('stock_sale_price') || 'Sale'}</th>
                      <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('stock_current') || 'Stock'}</th>
                      <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('stock_threshold') || 'Min'}</th>
                      <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('stock_status') || 'Status'}</th>
                      <th className="px-4 py-3 text-right font-semibold text-[#1e3a5f]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(p => {
                      const level = stockLevel(p);
                      const isEditing = editingRowId === p.id;
                      return (
                        <tr key={p.id} className={`border-b border-slate-50 transition-colors ${isEditing ? 'bg-blue-50/50' : 'hover:bg-slate-50'}`}
                          data-testid={`stock-row-${p.id}`}>
                          <td className="px-3 py-3">
                            <button onClick={() => toggleSelect(p.id)} className="p-0.5" data-testid={`stock-select-${p.id}`}>
                              {selectedIds.has(p.id)
                                ? <CheckSquare className="w-4 h-4 text-[#1e3a5f]" />
                                : <Square className="w-4 h-4 text-slate-300" />}
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-medium text-[#1e3a5f]">{p.name}</p>
                            {p.barcode && <p className="text-xs text-slate-400">{p.barcode}</p>}
                          </td>
                          <td className="px-4 py-3 text-slate-600 capitalize">{p.category}</td>
                          <td className="px-4 py-3">
                            {isEditing ? (
                              <input type="number" step="0.01" min="0"
                                value={editValues.cost_price}
                                onChange={e => setEditValues(v => ({ ...v, cost_price: e.target.value }))}
                                className="w-20 px-2 py-1 border-2 border-blue-300 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none"
                                data-testid={`edit-cost-${p.id}`} />
                            ) : (
                              <span className="text-slate-500">{p.cost_price ? `${p.cost_price.toFixed(2)}` : '--'}</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {isEditing ? (
                              <input type="number" step="0.01" min="0"
                                value={editValues.price}
                                onChange={e => setEditValues(v => ({ ...v, price: e.target.value }))}
                                className="w-20 px-2 py-1 border-2 border-blue-300 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none"
                                data-testid={`edit-price-${p.id}`} />
                            ) : (
                              <span className="font-medium text-[#22c55e]">{p.price?.toFixed(2) || '--'}</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {isEditing ? (
                              <input type="number" min="0"
                                value={editValues.stock_count}
                                onChange={e => setEditValues(v => ({ ...v, stock_count: e.target.value }))}
                                className="w-20 px-2 py-1 border-2 border-blue-300 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none"
                                data-testid={`edit-stock-${p.id}`} />
                            ) : (
                              <>
                                <span className="font-bold text-[#1e3a5f]">
                                  {p.stock_count !== null && p.stock_count !== undefined ? p.stock_count : '\u221E'}
                                </span>
                                <span className="text-xs text-slate-400 ml-1">{p.unit}</span>
                              </>
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-500">{p.low_stock_threshold || 5}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-sm text-xs font-medium ${stockBadge(level)}`}>
                              {level === 'out' ? (t('stock_out') || 'Out') :
                               level === 'low' ? (t('stock_low') || 'Low') :
                               level === 'ok' ? (t('stock_ok') || 'OK') :
                               (t('stock_unlimited') || '\u221E')}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1 justify-end">
                              {isEditing ? (
                                <>
                                  <button onClick={() => saveEdit(p.id)} disabled={saving}
                                    className="p-2 hover:bg-green-50 rounded-sm text-green-600"
                                    title="Save" data-testid={`save-edit-${p.id}`}>
                                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                  </button>
                                  <button onClick={cancelEdit}
                                    className="p-2 hover:bg-red-50 rounded-sm text-red-500"
                                    title="Cancel" data-testid={`cancel-edit-${p.id}`}>
                                    <X className="w-4 h-4" />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button onClick={() => startEdit(p)}
                                    className="p-2 hover:bg-blue-50 rounded-sm text-blue-600" title="Edit"
                                    data-testid={`stock-edit-${p.id}`}>
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                  <button onClick={() => { setAdjustProduct(p); setAdjustType('intake'); setAdjustQty(''); }}
                                    className="p-2 hover:bg-green-50 rounded-sm text-green-600" title={t('stock_add') || 'Add Stock'}
                                    data-testid={`stock-add-${p.id}`}>
                                    <Plus className="w-4 h-4" />
                                  </button>
                                  <button onClick={() => { setAdjustProduct(p); setAdjustType('adjustment'); setAdjustQty(''); }}
                                    className="p-2 hover:bg-amber-50 rounded-sm text-amber-600" title={t('stock_adjust') || 'Adjust'}
                                    data-testid={`stock-adjust-${p.id}`}>
                                    <ArrowUpDown className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Tab Content: Movement History */}
        {activeTab === 'movements' && (
          <div className="bg-white border-2 border-slate-100 overflow-hidden">
            {movements.length === 0 ? (
              <div className="p-8 text-center">
                <History className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-slate-500">{t('stock_no_movements') || 'No stock movements yet'}</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-50">
                {movements.map(m => {
                  const mtype = MOVEMENT_TYPES[m.movement_type] || MOVEMENT_TYPES.adjustment;
                  const MIcon = mtype.icon;
                  return (
                    <div key={m.id} className="flex items-center gap-4 p-4 hover:bg-slate-50" data-testid={`movement-${m.id}`}>
                      <div className={`w-10 h-10 rounded-sm flex items-center justify-center bg-slate-50 ${mtype.color}`}>
                        <MIcon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#1e3a5f]">{m.product_name}</p>
                        <p className="text-xs text-slate-400">
                          {t(mtype.label) || m.movement_type}
                          {m.supplier && ` \u00B7 ${m.supplier}`}
                          {m.notes && ` \u00B7 ${m.notes}`}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${m.quantity > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {m.quantity > 0 ? '+' : ''}{m.quantity}
                        </p>
                        <p className="text-xs text-slate-400">{m.previous_stock} \u2192 {m.new_stock}</p>
                      </div>
                      <p className="text-xs text-slate-400 hidden sm:block w-28 text-right">
                        {new Date(m.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Tab Content: AI Scanner */}
        {activeTab === 'scanner' && (
          <div className="space-y-4">
            <div className="bg-white border-2 border-slate-100 p-6 text-center">
              <Camera className="w-12 h-12 mx-auto mb-3 text-[#1e3a5f]" />
              <h3 className="text-lg font-bold text-[#1e3a5f] mb-1">{t('stock_scan_title') || 'AI Receipt Scanner'}</h3>
              <p className="text-sm text-slate-500 mb-4">{t('stock_scan_desc') || 'Upload a photo of a supplier receipt or delivery note to auto-extract products'}</p>
              <input type="file" ref={fileInputRef} accept="image/jpeg,image/png,image/webp"
                onChange={handleFileSelect} className="hidden" data-testid="receipt-file-input" />
              <button onClick={() => fileInputRef.current?.click()} disabled={scanning}
                className="vf-btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="upload-receipt-btn">
                {scanning ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                {scanning ? (t('stock_scanning') || 'Scanning...') : (t('stock_upload') || 'Upload Receipt')}
              </button>
            </div>

            {scanPreview && (
              <div className="bg-white border-2 border-slate-100 p-4">
                <img src={scanPreview} alt="Receipt" className="max-h-64 mx-auto rounded-sm border border-slate-200" />
              </div>
            )}

            {scanResult && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                className="bg-white border-2 border-slate-100 overflow-hidden" data-testid="scan-results">
                <div className="p-4 border-b border-slate-100">
                  <h4 className="font-semibold text-[#1e3a5f]">{t('stock_scan_results') || 'Scan Results'}</h4>
                  <div className="flex gap-4 mt-2 text-sm text-slate-500">
                    {scanResult.supplier_name && <span>{t('stock_supplier') || 'Supplier'}: <strong>{scanResult.supplier_name}</strong></span>}
                    {scanResult.receipt_date && <span>{t('inv_date') || 'Date'}: <strong>{scanResult.receipt_date}</strong></span>}
                    {scanResult.receipt_total && <span>Total: <strong>&euro;{scanResult.receipt_total}</strong></span>}
                  </div>
                </div>

                {scanResult.matched_items?.length > 0 && (
                  <div>
                    <div className="px-4 py-2 bg-green-50 text-sm font-medium text-green-700 flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      {t('stock_matched') || 'Matched Products'} ({scanResult.matched_items.length})
                    </div>
                    <div className="divide-y divide-slate-50">
                      {scanResult.matched_items.map((item, i) => (
                        <div key={i} className="flex items-center justify-between px-4 py-3 gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-[#1e3a5f]">{item.matched_name || item.name}</p>
                            <p className="text-xs text-slate-400">{item.category} &middot; {item.unit}</p>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <div className="text-center">
                              <p className="text-[10px] text-slate-400 mb-0.5">{t('stock_cost_price') || 'Cost'}</p>
                              <p className="text-sm text-slate-600">&euro;{item.cost_per_unit?.toFixed(2) || '--'}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-[10px] text-slate-400 mb-0.5">{t('stock_sale_price') || 'Sale'}</p>
                              <input type="number" step="0.01" min="0"
                                value={scanSalePrices[i] ?? item.existing_sale_price ?? ''}
                                onChange={e => setScanSalePrices(prev => ({ ...prev, [i]: parseFloat(e.target.value) || '' }))}
                                className="w-20 px-2 py-1 border-2 border-slate-200 rounded-sm text-sm text-center focus:border-[#22c55e] focus:outline-none"
                                placeholder="€0.00"
                                data-testid={`scan-sale-price-${i}`} />
                            </div>
                            <div className="text-right w-12">
                              <p className="font-bold text-green-600">+{item.quantity}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {scanResult.unmatched_items?.length > 0 && (
                  <div>
                    <div className="px-4 py-2 bg-amber-50 text-sm font-medium text-amber-700 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      {t('stock_unmatched') || 'New Items — Edit before adding'} ({scanResult.unmatched_items.length})
                    </div>
                    <div className="divide-y divide-slate-50">
                      {scanResult.unmatched_items.map((item, i) => {
                        const edits = scanEdits[`u${i}`] || {};
                        return (
                          <div key={i} className="px-4 py-3 space-y-2" data-testid={`unmatched-item-${i}`}>
                            <div className="flex items-center gap-3">
                              <div className="flex-1">
                                <input type="text"
                                  value={edits.name ?? item.name}
                                  onChange={e => setScanEdits(prev => ({ ...prev, [`u${i}`]: { ...prev[`u${i}`], name: e.target.value } }))}
                                  className="w-full px-2 py-1.5 border-2 border-amber-200 rounded-sm text-sm font-medium focus:border-[#1e3a5f] focus:outline-none"
                                  data-testid={`unmatched-name-${i}`} />
                              </div>
                              <div className="text-center">
                                <p className="text-[10px] text-slate-400 mb-0.5">Qty</p>
                                <input type="number" min="1" step="1"
                                  value={edits.quantity ?? (Math.ceil(item.quantity) || 1)}
                                  onChange={e => setScanEdits(prev => ({ ...prev, [`u${i}`]: { ...prev[`u${i}`], quantity: e.target.value } }))}
                                  className="w-16 px-2 py-1.5 border-2 border-amber-200 rounded-sm text-sm text-center font-bold focus:border-[#1e3a5f] focus:outline-none"
                                  data-testid={`unmatched-qty-${i}`} />
                              </div>
                              <div className="text-center">
                                <p className="text-[10px] text-slate-400 mb-0.5">Cost/u</p>
                                <input type="number" step="0.01" min="0"
                                  value={edits.cost_per_unit ?? item.cost_per_unit ?? ''}
                                  onChange={e => setScanEdits(prev => ({ ...prev, [`u${i}`]: { ...prev[`u${i}`], cost_per_unit: e.target.value } }))}
                                  className="w-20 px-2 py-1.5 border-2 border-amber-200 rounded-sm text-sm text-center focus:border-[#1e3a5f] focus:outline-none"
                                  data-testid={`unmatched-cost-${i}`} />
                              </div>
                              <div className="text-center">
                                <p className="text-[10px] text-slate-400 mb-0.5">Unit</p>
                                <select
                                  value={edits.unit ?? item.unit ?? 'unidad'}
                                  onChange={e => setScanEdits(prev => ({ ...prev, [`u${i}`]: { ...prev[`u${i}`], unit: e.target.value } }))}
                                  className="px-2 py-1.5 border-2 border-amber-200 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none"
                                  data-testid={`unmatched-unit-${i}`}>
                                  <option value="unidad">unidad</option>
                                  <option value="pack">pack</option>
                                  <option value="kg">kg</option>
                                  <option value="litro">litro</option>
                                  <option value="caja">caja</option>
                                </select>
                              </div>
                            </div>
                            <p className="text-xs text-slate-400 pl-1">
                              {item.category}{item.notes ? ` — ${item.notes}` : ''}
                              {item.total_cost ? ` — Total: €${item.total_cost.toFixed(2)}` : ''}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {(scanResult.matched_items?.length > 0 || scanResult.unmatched_items?.length > 0) && (
                  <div className="p-4 border-t border-slate-100 space-y-2">
                    {scanResult.matched_items?.length > 0 && (
                      <button onClick={handleBulkIntake} disabled={saving}
                        className="vf-btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                        data-testid="confirm-intake-btn">
                        {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                        {t('stock_confirm_intake') || 'Add Matched Items to Stock'} ({scanResult.matched_items.length})
                      </button>
                    )}
                    {scanResult.unmatched_items?.length > 0 && (
                      <button onClick={handleCreateUnmatched} disabled={saving}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-500 text-white font-medium hover:bg-amber-600 transition-colors disabled:opacity-50"
                        data-testid="create-unmatched-btn">
                        {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
                        Create & Add Unmatched as New Products ({scanResult.unmatched_items.length})
                      </button>
                    )}
                    <button onClick={() => { setScanResult(null); setScanPreview(null); }}
                      className="vf-btn-secondary w-full">{t('products_cancel') || 'Cancel'}</button>
                  </div>
                )}
              </motion.div>
            )}
          </div>
        )}

        {/* Tab Content: Reorder Suggestions */}
        {activeTab === 'reorder' && (
          <div className="space-y-4" data-testid="reorder-tab">
            {!reorderData ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" /></div>
            ) : reorderData.suggestions.length === 0 ? (
              <div className="bg-white border-2 border-slate-100 p-8 text-center">
                <Check className="w-12 h-12 mx-auto mb-3 text-[#22c55e]" />
                <h3 className="text-lg font-bold text-[#1e3a5f]">{t('stock_reorder_all_good') || 'All Stocked Up!'}</h3>
                <p className="text-sm text-slate-500">{t('stock_reorder_no_items') || 'No products need reordering right now.'}</p>
              </div>
            ) : (
              <>
                <div className="bg-white border-2 border-slate-100 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-[#1e3a5f]">{t('stock_reorder') || 'Reorder Suggestions'}</h3>
                      <p className="text-xs text-slate-400">{t('stock_reorder_desc') || 'Based on sales velocity from the last 30 days'}</p>
                    </div>
                    <span className="px-3 py-1 bg-amber-100 text-amber-700 text-sm font-medium rounded-sm">
                      {reorderData.total} {t('stock_items') || 'items'}
                    </span>
                  </div>
                </div>

                <div className="bg-white border-2 border-slate-100 overflow-hidden">
                  <table className="w-full text-sm" data-testid="reorder-table">
                    <thead>
                      <tr className="border-b-2 border-slate-100 text-left">
                        <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('stock_product') || 'Product'}</th>
                        <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('stock_current') || 'Stock'}</th>
                        <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('stock_reorder_velocity') || 'Daily Sales'}</th>
                        <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('stock_reorder_days_left') || 'Days Left'}</th>
                        <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('stock_reorder_qty') || 'Suggested Qty'}</th>
                        <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('stock_reorder_est_cost') || 'Est. Cost'}</th>
                        <th className="px-4 py-3 font-semibold text-[#1e3a5f]">{t('stock_reorder_urgency') || 'Urgency'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reorderData.suggestions.map((s, i) => (
                        <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <p className="font-medium text-[#1e3a5f]">{s.product_name}</p>
                            <p className="text-xs text-slate-400">{s.category}</p>
                          </td>
                          <td className="px-4 py-3 font-bold text-[#1e3a5f]">{s.current_stock}</td>
                          <td className="px-4 py-3 text-slate-600">{s.daily_velocity}/day</td>
                          <td className="px-4 py-3">
                            <span className={`font-medium ${
                              !s.days_until_stockout || s.days_until_stockout < 3 ? 'text-red-600' :
                              s.days_until_stockout < 7 ? 'text-amber-600' : 'text-slate-600'
                            }`}>
                              {s.days_until_stockout != null ? `${s.days_until_stockout} days` : '--'}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-semibold text-[#22c55e]">{s.suggested_quantity}</td>
                          <td className="px-4 py-3 text-slate-600">
                            {s.estimated_cost > 0 ? `\u20AC${s.estimated_cost.toFixed(2)}` : '--'}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-sm text-xs font-medium ${
                              s.urgency === 'critical' ? 'bg-red-100 text-red-700' :
                              s.urgency === 'urgent' ? 'bg-amber-100 text-amber-700' :
                              s.urgency === 'soon' ? 'bg-blue-100 text-blue-700' :
                              'bg-green-100 text-green-700'
                            }`} data-testid={`urgency-${s.urgency}`}>
                              {t(`stock_urgency_${s.urgency}`) || s.urgency}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Adjust Stock Modal */}
      <AnimatePresence>
        {adjustProduct && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setAdjustProduct(null)}>
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="w-[420px] bg-white p-6"
                data-testid="adjust-stock-modal"
                onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-bold text-[#1e3a5f] mb-1">{t('stock_adjust_title') || 'Adjust Stock'}</h3>
              <p className="text-sm text-slate-500 mb-4">{adjustProduct.name} -- {t('stock_current') || 'Current'}: <strong>{adjustProduct.stock_count ?? '\u221E'}</strong></p>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('stock_type') || 'Type'}</label>
                  <select value={adjustType} onChange={e => setAdjustType(e.target.value)}
                    className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none"
                    data-testid="adjust-type-select">
                    <option value="intake">{t('stock_intake') || 'Intake (+)'}</option>
                    <option value="adjustment">{t('stock_adjustment') || 'Adjustment'}</option>
                    <option value="loss">{t('stock_loss') || 'Loss (-)'}</option>
                    <option value="return">{t('stock_return') || 'Return (+)'}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('inv_quantity') || 'Quantity'}</label>
                  <input type="number" min="1" value={adjustQty} onChange={e => setAdjustQty(e.target.value)}
                    className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none"
                    data-testid="adjust-qty-input" placeholder="0" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('stock_supplier') || 'Supplier'}</label>
                  <input type="text" value={adjustSupplier} onChange={e => setAdjustSupplier(e.target.value)}
                    className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('stock_cost') || 'Cost/Unit (\u20AC)'}</label>
                  <input type="number" step="0.01" value={adjustCost} onChange={e => setAdjustCost(e.target.value)}
                    className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('stock_sale_price') || 'Sale Price (\u20AC)'}</label>
                  <input type="number" step="0.01" value={adjustSalePrice}
                    onChange={e => setAdjustSalePrice(e.target.value)}
                    placeholder={adjustProduct?.price ? `${t('stock_current') || 'Current'}: \u20AC${adjustProduct.price.toFixed(2)}` : ''}
                    className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none"
                    data-testid="adjust-sale-price" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1e3a5f] mb-1">{t('inv_notes') || 'Notes'}</label>
                  <input type="text" value={adjustNotes} onChange={e => setAdjustNotes(e.target.value)}
                    className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none" />
                </div>
              </div>
              <div className="flex gap-3 mt-5">
                <button onClick={() => setAdjustProduct(null)} className="vf-btn-secondary flex-1">{t('products_cancel') || 'Cancel'}</button>
                <button onClick={handleAdjust} disabled={saving || !adjustQty}
                  className="vf-btn-primary flex-1 disabled:opacity-50" data-testid="confirm-adjust-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {t('stock_confirm') || 'Confirm'}
                </button>
              </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Bulk Edit Modal */}
      <AnimatePresence>
        {showBulkEdit && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowBulkEdit(false)}>
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                className="w-[440px] bg-white p-6"
                data-testid="bulk-edit-modal"
                onClick={e => e.stopPropagation()}>
                <h3 className="text-lg font-bold text-[#1e3a5f] mb-1">Bulk Edit Products</h3>
                <p className="text-sm text-slate-500 mb-4">Update {selectedIds.size} selected products. Leave fields empty to skip.</p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#1e3a5f] mb-1">New Stock Quantity</label>
                    <input type="number" min="0" value={bulkValues.stock_count}
                      onChange={e => setBulkValues(v => ({ ...v, stock_count: e.target.value }))}
                      className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none"
                      placeholder="Leave empty to skip" data-testid="bulk-stock-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1e3a5f] mb-1">New Cost Price (€)</label>
                    <input type="number" step="0.01" min="0" value={bulkValues.cost_price}
                      onChange={e => setBulkValues(v => ({ ...v, cost_price: e.target.value }))}
                      className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none"
                      placeholder="Leave empty to skip" data-testid="bulk-cost-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1e3a5f] mb-1">New Sale Price (€)</label>
                    <input type="number" step="0.01" min="0" value={bulkValues.price}
                      onChange={e => setBulkValues(v => ({ ...v, price: e.target.value }))}
                      className="w-full px-3 py-2 border-2 border-slate-200 rounded-sm focus:border-[#1e3a5f] focus:outline-none"
                      placeholder="Leave empty to skip" data-testid="bulk-price-input" />
                  </div>
                </div>
                <div className="flex gap-3 mt-5">
                  <button onClick={() => setShowBulkEdit(false)} className="vf-btn-secondary flex-1">Cancel</button>
                  <button onClick={saveBulkEdit} disabled={bulkSaving || (bulkValues.stock_count === '' && bulkValues.cost_price === '' && bulkValues.price === '')}
                    className="vf-btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="confirm-bulk-edit-btn">
                    {bulkSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Update {selectedIds.size} Products
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </ShopLayout>
  );
};

export default StockPage;
